import BootstrapModal from "bootstrap/js/dist/modal";

import { UnshadowedElement, html } from "../lib/unshadowed.js";

import { OutsideClickWatcher } from "../lib/outside-clicks.js";
import { PostJSON } from "../lib/fetchers.js";
import { IsoDateFormatter } from "../lib/date-utilities.js";
import { TrackClickById } from "../lib/click-tracking.js";

import { UserAvatar } from "./user-avatar.js";
import { ShareOptions } from "./share-options.js";
import { TextRevealer } from "./text-revealer.js";
import { PostLikesDisplay } from "./post-likes-display";
import { PostLikeButton } from "./post-like-button";

UserAvatar.Register();
ShareOptions.Register();
TextRevealer.Register();
PostLikesDisplay.Register();
PostLikeButton.Register();

/**
 * Full display of a single train update post
 * Expects the entity property to be a vwPost
 */
export class TrainPost extends UnshadowedElement {
    static elementName() { return "train-post" }

    static properties = {
        entity: { type: Object },
        isInModal: { type: Boolean },
        showingMoreActions: { state: true, type: Boolean },
        showingDeleteConfirm: { state: true, type: Boolean },
        showingShare: { state: true, type: Boolean },
    };

    async PerformDelete() {
        const button = this.querySelector("[data-button-delete]");
        if (button) button.classList.add("button-loading");
        try {
            await PostJSON(`/trains/${this.entity.Post.TrainId}/updates/${this.entity.Post.Id}/delete`);
            this.showingDeleteConfirm = false;
            window.location = `/trains/${this.entity.Post.TrainId}/updates`;
        } catch (err) {
            const errorRow = this.querySelector("[data-error-message]");
            if (err && err.message) {
                errorRow.querySelector(".alert-message").innerText = err.message;
            }
            errorRow.classList.remove("hidden");
            console.error(err);
            if (button) button.classList.remove("button-loading");
        }
    }

    render() {
        /** Contains the action buttons available for this post */
        let adminButton = null;
        if (window.CurrentTrain.IsAdmin && !this.isInModal) {
            adminButton = html`
                <button @click=${() => { this.showingMoreActions = true; }} class="button button-rectangle button-condensed button-outline show-context-menu" style="--this-padding: 0 0.35em;">
                    <svg role="img" width="24" height="24" aria-label="More"><use xlink:href="#meatball-blue" /></svg>
                </button>
            `;
        }

        /** If we expanded the "..." button, contains code for the popout tooltip menu */
        let moreActionsPopout = null;
        if (this.showingMoreActions) {
            moreActionsPopout = html`
                <nav id="popoutMoreActions" class="context-menu">
                    <ul>
                        <li><button @click=${() => { this.showingShare = true; this.showingMoreActions = false; }} class="invisible-button context-share-button"><svg role="img" width="24" height="24" aria-label="Share"><use xlink:href="#share-generic-blue" /></svg> Share</a></li>
                        <li><a class="invisible-button edit-button" href="/trains/${this.entity.Post.TrainId}/updates/${this.entity.Post.Id}/edit"><svg role="img" width="24" height="24" aria-label="Edit"><use xlink:href="#edit-blue" /></svg> Edit</a></li>
                        <li><button class="invisible-button delete-button" @click=${() => { this.showingDeleteConfirm = true; this.showingMoreActions = false; }}><svg role="img" width="24" height="24" aria-label="More"><use xlink:href="#trash-blue" /></svg> Delete</a></li>
                    </ul>
                </nav>`;
        }

        /** If we are confirming post deletion, contains code for the confirmation modal */
        let deleteConfirmModal = null;
        if (this.showingDeleteConfirm) {
            deleteConfirmModal = html`
                <div id="deleteConfirmModal" data-bs-backdrop="static" class="modal fade confirm-dialog">
                    <div class="modal-dialog modal-sm">
                        <div class="modal-content">
                            <div class="modal-header">
                                <h1 class="modal-title">Confirm Delete</h1>
                                <button class="btn-close" data-bs-dismiss="modal"></button>
                            </div>
                            <div class="modal-body">
                                <p>Are you sure you want to delete this post? You won't be able to undo this.</p>
                                <div class="hidden alert alert-danger alert-with-icon" data-error-message role="alert">
                                    <svg role="img" width="24" height="24" aria-label="Error:">
                                        <use xlink:href="#participant-error" />
                                    </svg>
                                    <span class="alert-message">There was a problem removing this update, try again later.</span>
                                </div>
                                <div class="modal-button-row">
                                    <button class="button button-rectangle button-outline" data-bs-dismiss="modal">Close</button>
                                    <button @click=${this.PerformDelete} href="/trains/${window.CurrentTrain.Id}/volunteer/${this.entity.Post.Id}" data-button-delete class="button button-rectangle button-destructive">
                                        <svg role="img" width="24" height="24" aria-label="Delete"><use xlink:href="#trash-white" /></svg>Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>`;
        }

        /** Code for the share modal, if we are showing it */
        let shareModal = null;
        if (this.showingShare) {
            const updateurl = window.CurrentTrain.FullUrl + "/updates/" + this.entity.Post.Id;
            let imageHolder = html`<div class="image-holder train-thumbnail-photo placeholder-mt-icon"></div>`;
            const photoPath = this.entity.Post.PhotoPath || window.CurrentTrain.PhotoUrl;
            if (photoPath) {
                imageHolder = html`<div class="image-holder"><img src="${photoPath}" alt="${this.entity.Post.Name}" title="${this.entity.Post.Name}" /></div>`;
            }
            shareModal = html`
                <div id="shareModal" class="modal fade train-share-modal train-share-modal-update">
                    <div class="modal-dialog">
                        <div class="modal-content">
                            <div class="modal-header">
                                <h1 class="modal-title" id="modalTrainShareTitle">
                                    Share Update
                                </h1>
                                <button class="btn-close" data-bs-dismiss="modal"></button>
                            </div>
                            <div class="modal-body">
                                <div class="train-info">
                                    <div class="train-info-card">
                                        ${imageHolder}
                                        <div class="card-content">
                                            <p>Meal Train Update for ${window.CurrentTrain.Name}</p>
                                            <p class="train-share-modal__subtitle">${this.entity.Post.Title}</p>
                                        </div>
                                    </div>
                                </div>
                                <share-options name="${this.entity.Post.Title}" url="${updateurl}" hideFlyer hideInvite itemType="Meal Train update"></share-options>
                            </div>
                        </div>
                    </div>
                </div>`;
        }
        return html`
            <div class="train-update">
                <h3 class="train-update__title">${this.entity.Post.Title}</h3>
                <div class="train-update__header-row">
                    <user-avatar avatar=${this.entity.Post.Author.Avatar} initials=${this.entity.Post.Author.Initials}></user-avatar>
                    <p><span class="train-update__date">${IsoDateFormatter.LongDate(this.entity.Post.DateLocal)}</span> by <span>${this.entity.Post.Author.FullName}</span></p>
                    <div class="train-update__actions">
                        ${adminButton}
                        ${moreActionsPopout}
                    </div>
                </div>
                <text-revealer cutoff="300" class="train-update__content" text=${this.entity.Post.Text}></text-revealer>
                ${this.entity.Post.PhotoPath ? html`<div data-photo class="train-update__photo-holder"><img src=${this.entity.Post.PhotoPath}></div>` : null}
                <post-likes-display .entity=${this.entity} ?isInModal=${this.isInModal} class="reactions-display"></post-likes-display>
                <div class="train-update__actions">
                <post-like-button .entity=${this.entity}></post-like-button>
                <button @click=${() => { this.showingShare = true; }} class="button button-outline button-rectangle share-button">
                    <svg class="hide-platform-android" role="img" width="24" height="24" aria-label="Share Update">
                        <use xlink:href="#share-options-ios-blue"></use>
                    </svg>
                    <svg class="show-platform-android" role="img" width="24" height="24" aria-label="Share Update">
                        <use xlink:href="#share-options-android-blue"></use>
                    </svg>               
                Share</button>
                </div>
                ${deleteConfirmModal}
                ${shareModal}
            </div>
        `;
    }

    clickOutsideHandlerId = null;

    // Clean up after DOM elements are created
    updated() {
        const deleteModal = document.getElementById("deleteConfirmModal");
        // If we created the modal, have to show it via bootstrap JS
        if (deleteModal) {
            const modal = BootstrapModal.getOrCreateInstance(deleteModal);
            modal.show();
            // When the modal is hidden, turn off showingModalDay so we don't reshow the modal
            deleteModal.addEventListener("hidden.bs.modal", () => { this.showingDeleteConfirm = false; });
        }

        const shareModal = document.getElementById("shareModal");
        // If we created the modal, have to show it via bootstrap JS
        if (shareModal) {
            TrackClickById("btn-share-update");
            const modal = BootstrapModal.getOrCreateInstance(shareModal);
            modal.show();
            // When the modal is hidden, turn off showingModalDay so we don't reshow the modal
            shareModal.addEventListener("hidden.bs.modal", () => { this.showingShare = false; });
        }

        if (this.showingMoreActions) {
            const moreActions = document.getElementById("popoutMoreActions");
            if (moreActions) {
                // Click event propagates upwards, so window.click fires after the listener is created. Have to ignore the first immediate click event.
                setTimeout(() => { this.clickOutsideHandlerId = OutsideClickWatcher.AddListener(moreActions, () => { this.showingMoreActions = false; }); }, 100);
            }
        } else {
            // If we previously registered a click outside handler, remove it
            if (this.clickOutsideHandlerId) {
                OutsideClickWatcher.RemoveListener(this.clickOutsideHandlerId);
                this.clickOutsideHandlerId = null;
            }
        }
    }
}
